import _ from 'lodash'
import React, { useContext, useCallback, useRef, useEffect } from 'react'
import { findDOMNode } from 'react-dom'
import { useDrop } from 'react-dnd'
import Draggable from './Draggable'
import { SortableContext } from './SortableProvider'

const defaultIsItemSortable = () => true

const Sortable = React.memo(function Sortable ({
  setSortable,
  removeSortable,

  children,

  containerTag: ContainerTag,
  name,
  dragType,
  component,
  id,
  items,
  vertical,
  horizontal,
  disabled,
  droppable,
  onPositionChange,
  isItemSortable,

  ...restProps
}) {
  const requestedFrame = useRef(null)
  const pendingUpdateFn = useRef(null)

  useEffect(() => {
    return () => {
      cancelAnimationFrame(requestedFrame.current)
    }
  }, [])

  const drawFrame = useCallback(
    () => {
      pendingUpdateFn.current()
    
      pendingUpdateFn.current = null
      requestedFrame.current = null
    },
    []
  )

  const scheduleUpdate = useCallback(
    (updateFn) => {
      pendingUpdateFn.current = updateFn
    
      if (!requestedFrame.current) {
        requestedFrame.current = requestAnimationFrame(drawFrame)
      }
    },
    [drawFrame]
  )

  const itemRefs = useRef({})

  const itemsRef = useRef(items)
  useEffect(() => {
    itemsRef.current = items
  }, [items])

  const handleSortStart = useCallback(
    ({
      itemId,
      parentId,
      index,
    }) => {
      setSortable({
        name,
        itemId,
        parentId,
        index,
      })
    },
    [name, setSortable]
  )

  const handleSortChange = useCallback(
    ({
      itemId,
      parentId,
      index,
    }) => {
      setSortable({
        name,
        itemId,
        parentId,
        index,
      })
    },
    [name, setSortable]
  )

  const handleSortEnd = useCallback(
    ({
      itemId,
      parentId,
      index,
      droppedOnDroppable
    }) => {
      cancelAnimationFrame(requestedFrame.current)
      pendingUpdateFn.current = null
      requestedFrame.current = null

      if (!droppedOnDroppable && onPositionChange) {
        onPositionChange({
          itemId,
          parentId,
          position: index,
          items: itemsRef.current,
        })
      }

      removeSortable(name)
    },
    [name, removeSortable, onPositionChange]
  )

  const [, drop] = useDrop({
    accept: dragType || name,
    canDrop: () => true,
    drop: (item) => item,
    hover: (_item, monitor) => {
      if (!droppable) return

      const clientOffset = monitor.getClientOffset()

      scheduleUpdate(() => {
        const item = monitor.getItem()

        if (!item) return

        const { itemId, parentId } = item
   
        if (disabled) return
      
        let newIndex = null
      
        if (id === parentId) {
          const draggableDOMNode = findDOMNode(itemRefs.current[itemId])
      
          if (!draggableDOMNode) return
      
          const draggableBoundingRect = draggableDOMNode.getBoundingClientRect()
        
          const itemsLength = items.length
          let index
          for (index = 0; index < itemsLength; index++) {
            if (!isItemSortable(items[index])) continue

            if (items[index] === itemId) continue
      
            const hoverDOMNode = findDOMNode(itemRefs.current[items[index]])
      
            if (!hoverDOMNode) continue
      
            const hoverBoundingRect = hoverDOMNode.getBoundingClientRect()
      
            let verticalPassed = true
            if (vertical) {
              // Get vertical middle
              const hoverMiddle = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
  
              // Get pixels to the top
              const hoverClient = clientOffset.y - hoverBoundingRect.top
      
              // Only perform the move when the mouse has crossed half of the items height
              if (draggableBoundingRect.top < hoverBoundingRect.top) {
                verticalPassed = hoverClient > hoverMiddle
              }
      
              if (draggableBoundingRect.top > hoverBoundingRect.top) {
                verticalPassed = hoverClient < hoverMiddle
              }
            }
      
            let horizontalPassed = true
            if (horizontal) {
              // Get horizontal middle
              const hoverMiddle = (hoverBoundingRect.right - hoverBoundingRect.left) / 2
          
              // Get pixels to the top
              const hoverClient = clientOffset.x - hoverBoundingRect.left
      
              // Only perform the move when the mouse has crossed half of the items width
              if (draggableBoundingRect.left < hoverBoundingRect.left) {
                horizontalPassed = hoverClient > hoverMiddle
              }
      
              if (draggableBoundingRect.left > hoverBoundingRect.left) {
                horizontalPassed = hoverClient < hoverMiddle
              }
            }
      
            if (verticalPassed && horizontalPassed) {
              newIndex = index
      
              break
            }
          }
        } else {      
          const itemsLength = items.length
          let index
          for (index = 0; index < itemsLength; index++) {
            const hoverDOMNode = findDOMNode(itemRefs.current[items[index]])
      
            if (!hoverDOMNode) continue
      
            const hoverBoundingRect = hoverDOMNode.getBoundingClientRect()
      
            let verticalPassed = true
            if (vertical) {
              if (!(clientOffset.y < hoverBoundingRect.bottom)) {
                verticalPassed = false
              }
            }
      
            let horizontalPassed = true
            if (horizontal) {
              if (!(clientOffset.x < hoverBoundingRect.right)) {
                horizontalPassed = false
              }
            }
      
            if (verticalPassed && horizontalPassed) {
              newIndex = index
      
              break
            }
          }
      
          if (newIndex === null) newIndex = itemsLength
        }
      
        if (newIndex !== null) {
          item.parentId = id
          item.index = newIndex
          item.onSortEnd = handleSortEnd
      
          handleSortChange({
            itemId,
            parentId: id,
            index: newIndex
          })
        }
      })
    }
  })

  const renderItem = useCallback(
    (props) => {
      return (
        <Draggable
          {...props}
          ref={(ref) => { itemRefs.current[props.id] = ref }}
          component={component}
          parentId={id}
          dragType={dragType || name}
          vertical={vertical}
          horizontal={horizontal}
          sortDisabled={disabled || !isItemSortable(props.id)}
          onSortStart={handleSortStart}
          onSortEnd={handleSortEnd}
        />
      )
    },
    [component, disabled, dragType, handleSortEnd, handleSortStart, horizontal, id, name, vertical, isItemSortable]
  )

  const renderContainer = useCallback(
    () => {
      return (
        <ContainerTag
          {...restProps}
          ref={drop}
        >
          {(component && items && items.length > 0) && items.map((itemId, index) =>
            renderItem({
              key: itemId,
              id: itemId,
              index
            })
          )}
        </ContainerTag>
      )
    },
    [component, drop, items, renderItem, restProps]
  )

  if (children) {
    return children({
      renderContainer: renderContainer,
      renderItem: renderItem,
      items,
      connectDropTarget: drop
    })
  } else {
    return renderContainer()
  }
})

Sortable.defaultProps = {
  containerTag: 'div',
  animated: true,
  droppable: true,
  isItemSortable: defaultIsItemSortable,
}

function SortableContextConsumer ({
  id,
  name,
  items: rawItems,
  ...props
}) {
  const {
    sortable,
    setSortable,
    removeSortable
  } = useContext(SortableContext)

  const itemsRef = useRef(null)

  const currentSortable = sortable[name]

  if (itemsRef.current === null) {
    let _items = rawItems

    if (currentSortable) {
      const index = _items.indexOf(currentSortable.itemId)
      if (index !== -1) {
        _items = [..._items]
        _items.splice(index, 1)
      }

      if (currentSortable.parentId === id) {
        _items = [..._items]
        _items.splice(currentSortable.index, 0, currentSortable.itemId)
      }
    }

    itemsRef.current = _items
  } else {
    let _items = rawItems

    if (currentSortable) {
      const index = _items.indexOf(currentSortable.itemId)
      if (index !== -1) {
        _items = [..._items]
        _items.splice(index, 1)
      }

      if (currentSortable.parentId === id) {
        _items = [..._items]
        _items.splice(currentSortable.index, 0, currentSortable.itemId)
      }
    }

    if (!_.isEqual(itemsRef.current, _items)) {
      itemsRef.current = _items
    }
  }

  return (
    <Sortable
      {...props}
      id={id}
      name={name}
      items={itemsRef.current}
      setSortable={setSortable}
      removeSortable={removeSortable}
    />
  )
}

SortableContextConsumer.defaultProps = {
  id: 0,
}

export default SortableContextConsumer
